import { RATINGS } from '@common/constants'
import Footer from '@components/common/footer'
import I18nNextSeo from '@components/common/i18n/i18n-next-seo'
import NavBar from '@components/common/navbar'
import Articles from '@components/home/articles'
import BookFreeClassFooterBanner from '@components/home/book-free-class-footer-banner'
import CompetitionsFold from '@components/home/competitions-fold'
import Faq from '@components/home/faq'
import FirstFold from '@components/home/first-fold'
import Founders from '@components/home/founders'
import MoneyBackGuarantee from '@components/home/guarantee'
import LearningDashboard from '@components/home/learning-dashboard'
import StudentProjectsFold from '@components/home/student-projects-fold'
import Teachers from '@components/home/teachers'
import TrustedPlatform from '@components/home/trusted-platform'
import WhyLearnToCode from '@components/home/why-learn-to-code'
import { FAQ_CONTENTS } from '@lib/data/faq'
import { homePageSeo } from '@lib/seo'
import { isTrustpilotRatingEnabled } from '@lib/utils/feature-flag'
import { FAQPageJsonLd, OrganizationJsonLd } from 'next-seo'
import Courses, { HomepageCourse } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import useDetectCountry from '@hooks/useDetectCountry'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { formatLocale } from '@lib/utils/i18n'
import CodingCertificate from '@components/home/coding-certificate'
import CodingClassCatalog from '@components/home/coding-class-catalog'
import CodingalSubscription from '@components/home/codingal-subscription'
import { Investors } from '@components/book-trial-class-jp/investors'
import CodingalProductBlog from '@components/blog/product-blogs'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import TeacherStrip from '@components/home/teacher-strip'
import { GetStaticProps } from 'next'
import { CClient } from '@common/utils'
import * as Sentry from '@sentry/node'
import PersonalizedTrialStrip from '@components/common/personalized-trial-strip'

type DefaultHomeProps = {
  homepageCourses: HomepageCourse[]
}

const DefaultHome = ({ homepageCourses }: DefaultHomeProps) => {
  const { countryCode } = useDetectCountry()
  const router = useRouter()
  const [localCountryCode, setLocalCountryCode] = useState(countryCode)

  useEffect(() => {
    const locale = formatLocale(router?.locale)

    if (locale === 'en-PK') {
      setLocalCountryCode('PK')
    } else if (locale === 'en-US') {
      setLocalCountryCode('US')
    } else if (locale === 'en-CA') {
      setLocalCountryCode('CA')
    }
  }, [router?.locale])

  return (
    <>
      <I18nNextSeo {...homePageSeo} />
      <OrganizationJsonLd
        id="https://www.codingal.com/#org"
        name="Codingal"
        url="https://www.codingal.com/"
        logo="https://fastly.codingal.com/images/logos/logos-main/logo-with-text.svg"
        description="AI & coding classes for kids and teens, live online."
        sameAs={[
          'https://www.facebook.com/codingal.education',
          'https://x.com/codingal',
          'https://www.linkedin.com/company/codingal',
        ]}
        aggregateRating={
          isTrustpilotRatingEnabled()
            ? {
                '@type': 'AggregateRating',
                ratingValue: RATINGS.TRUSTPILOT_RATING.rating,
                bestRating: '5',
                ratingCount: RATINGS.TRUSTPILOT_RATING.ratingCount,
              }
            : undefined
        }
      />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText: acceptedAnswerTextString,
            questionName: questionName,
          })
        )}
      />
      <div data-testid="home-page">
        <NavBar isSticky />
        <FirstFold localeCountryCode={localCountryCode} />
        <TeacherStrip />
        {/* The trial strip below supplies the bottom spacing */}
        <Courses
          homepageCourses={homepageCourses}
          className="bg-white"
          paddingClassName="pt-12 lg:pt-16"
        />
        <PersonalizedTrialStrip />
        {/* flex stops the section's vertical margins collapsing out of the
            grey background */}
        <div className="bg-grey-100 flex flex-col">
          <CodingalSubscription />
        </div>
        <div style={{ backgroundColor: '#F4FAFF' }} className="pb-4">
          <VideoTestimonial
            title={
              <Trans>
                Students and parents love Codingal’s training program and
                curriculum
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview />
        </div>
        <Teachers
          title={
            <Trans>
              Learn to code from experienced teachers. Our hand-picked tutors
              ensure the best learning experience
            </Trans>
          }
        />
        <MoneyBackGuarantee />
        <CodingCertificate />
        <WhyLearnToCode />
        <StudentProjectsFold />
        <LearningDashboard />
        <div className="bg-blue-100 bg-opacity-25">
          <CompetitionsFold shouldShowPartnerLogos />
        </div>
        <Founders />
        <Investors ctaSize="xl" />
        <div className="bg-orange-100">
          <TrustedPlatform />
        </div>
        <Articles />
        <Faq />
        <CodingClassCatalog />
        <CodingalProductBlog />
        <div className="-mb-10">
          <BookFreeClassFooterBanner />
        </div>
        <Footer />
      </div>
    </>
  )
}

// Statically generate the homepage with the dynamic courses baked into the
// HTML (fast load + SEO), and regenerate at most once every 10 min via ISR so
// admin changes to the homepage course list still show up. The homepage course
// list changes rarely, so a 10 min window keeps regeneration load low across
// the fleet; redeploy if an edit needs to go live sooner.
export const getStaticProps: GetStaticProps<DefaultHomeProps> = async () => {
  try {
    const response = await CClient('/api/v1/courses/?homepage=true')

    if (!response.ok) {
      throw new Error(`Failed to fetch homepage courses: ${response.status}`)
    }

    const data = await response.json()

    return {
      props: {
        homepageCourses: data.results || data || [],
      },
      revalidate: 600,
    }
  } catch (error) {
    console.error('Error fetching homepage courses:', error)
    Sentry.captureException(error)

    // Re-throw instead of returning empty courses. During ISR regeneration a
    // thrown error makes Next discard the regeneration and keep serving the
    // last-good cached page (retrying on the next request), so a transient API
    // blip does not get cached as an empty homepage for the whole revalidate
    // window. At build time (no prior cache) this surfaces an API outage as a
    // failed build rather than silently shipping an empty courses section
    // across every locale.
    throw error
  }
}

export default DefaultHome
